import React, { useState } from 'react';
import {
  Modal, ModalDialog, ModalClose, Stack, Typography, FormControl,
  FormLabel, Select, Option, Button, Chip, Divider, Box, CircularProgress,
} from '@mui/joy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../../lib/api';
import type { DripCampaign, DripEnrollment } from '../../../types/marketing';

interface Props {
  open: boolean;
  onClose: () => void;
  contactId: number;
  contactName: string;
}

const STATUS_COLOR: Record<DripEnrollment['status'], 'success' | 'neutral' | 'danger' | 'warning'> = {
  active: 'success', completed: 'neutral', canceled: 'neutral', unsubscribed: 'danger',
};

export const DripEnrollmentModal: React.FC<Props> = ({
  open, onClose, contactId, contactName,
}) => {
  const qc = useQueryClient();
  const [selectedDrip, setSelectedDrip] = useState<number | null>(null);

  const { data: enrollments = [], isLoading: enrollmentsLoading } = useQuery({
    queryKey: ['contact-drips', contactId],
    queryFn: async () => (await api.getContactDripEnrollments(contactId)).data,
    enabled: open,
  });

  const { data: drips = [] } = useQuery({
    queryKey: ['drip-campaigns'],
    queryFn: async () => (await api.getDripCampaigns()).data,
    enabled: open,
  });

  const activeDrips = drips.filter((d: DripCampaign) => d.is_active);

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ['contact-drips', contactId] });

  const add = useMutation({
    mutationFn: async () =>
      api.enrollDripContacts(selectedDrip!, { contact_ids: [contactId] }),
    onSuccess: (r) => {
      refresh();
      if (r.data.enrolled === 0) {
        toast.error(
          'Not enrolled. The contact may have no email, be opted out of '
          + 'email, or already be enrolled.'
        );
      } else {
        toast.success(r.data.detail);
        setSelectedDrip(null);
      }
    },
    onError: () => toast.error('Failed to enroll contact'),
  });

  const cancel = useMutation({
    mutationFn: async (enrollmentId: number) =>
      api.cancelDripEnrollment(enrollmentId),
    onSuccess: () => { refresh(); toast.success('Enrollment canceled'); },
    onError: () => toast.error('Failed to cancel enrollment'),
  });

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 520, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        <Typography level="h4">Drip Campaigns</Typography>
        <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 1 }}>
          {contactName}
        </Typography>

        <Typography level="title-sm" sx={{ mt: 1 }}>Current enrollments</Typography>
        {enrollmentsLoading ? (
          <CircularProgress size="sm" sx={{ my: 1 }} />
        ) : enrollments.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            Not enrolled in any drip campaigns.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {enrollments.map((e) => (
              <Box key={e.id}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography level="body-sm" noWrap>
                    {e.drip_campaign_name || `Drip #${e.drip_campaign}`}
                  </Typography>
                  <Chip size="sm" variant="soft" color={STATUS_COLOR[e.status]}>
                    {e.status} · {e.source}
                  </Chip>
                </Box>
                {e.status === 'active' && (
                  <Button size="sm" variant="plain" color="danger"
                    loading={cancel.isPending}
                    onClick={() => cancel.mutate(e.id)}>
                    Remove
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography level="title-sm">Add to a drip campaign</Typography>
        {activeDrips.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.5 }}>
            No active drip campaigns. Activate one in Marketing → Email Marketing.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mt: 0.5 }}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Drip campaign</FormLabel>
              <Select placeholder="Select a drip…" value={selectedDrip}
                onChange={(_, v) => setSelectedDrip(v as number)}>
                {activeDrips.map((d: DripCampaign) => (
                  <Option key={d.id} value={d.id}>{d.name}</Option>
                ))}
              </Select>
            </FormControl>
            <Button disabled={!selectedDrip} loading={add.isPending}
              onClick={() => add.mutate()}>
              Add
            </Button>
          </Stack>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="plain" onClick={onClose}>Close</Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
